import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Chip, Button, Modal, Fade, TextField, IconButton,
  CircularProgress, Tabs, Tab, Snackbar, alpha, useTheme,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFileRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import HourglassTopIcon from '@mui/icons-material/HourglassTopRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { motion } from 'framer-motion';
import {
  getAssignments, getAssignmentDetail, submitAssignment, updateAssignmentSubmission,
  getMySubmissions, getSubmissionDetail, deleteSubmission, getAssignmentPdfUrl, getAdmissionId,
} from '../api/studentApi';
import EmptyState from '../components/EmptyState';
import { tokens } from '../theme/tokens';
import { staggerContainer, fadeInUp, hoverLift } from '../utils/motion';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

const statusMeta = (status) => {
  const s = (status || 'submitted').toLowerCase();
  if (s === 'reviewed') return { color: tokens.mint, icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
  if (s === 'pending') return { color: tokens.amber, icon: <HourglassTopIcon sx={{ fontSize: 16 }} /> };
  return { color: tokens.indigo, icon: <AssignmentTurnedInIcon sx={{ fontSize: 16 }} /> };
};

const Assignments = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0); // 0 = Assignments, 1 = My Submissions
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const [submitOpen, setSubmitOpen] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [editingSubmissionId, setEditingSubmissionId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const admissionId = getAdmissionId();

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const res = await getAssignments();
      setAssignments(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const res = await getMySubmissions();
      setSubmissions(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 0) loadAssignments();
    else loadSubmissions();
  }, [tab]);

  const handleViewDetail = async (assignment) => {
    try {
      const res = await getAssignmentDetail(assignment.id);
      setSelectedDetail(res.data);
      setDetailOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleViewSubmission = async (submissionId) => {
    try {
      const res = await getSubmissionDetail(submissionId);
      setSelectedDetail(res.data);
      setDetailOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenSubmit = (assignment, existingSubmission = null) => {
    setActiveAssignment(assignment);
    setEditingSubmissionId(existingSubmission?.id || null);
    setNote(existingSubmission?.note || '');
    setFile(null);
    setSubmitOpen(true);
  };

  const handleSubmit = async () => {
    if (!file && !editingSubmissionId) {
      setSnackbar({ open: true, message: 'Please attach a file.', severity: 'error' });
      return;
    }
    const formData = new FormData();
    formData.append('admission', admissionId);
    formData.append('assignment', activeAssignment.id);
    if (note) formData.append('note', note);
    if (file) formData.append('file', file);
    if (editingSubmissionId) formData.append('id', editingSubmissionId);

    try {
      if (editingSubmissionId) {
        await updateAssignmentSubmission(formData);
        setSnackbar({ open: true, message: 'Submission updated!', severity: 'success' });
      } else {
        await submitAssignment(formData);
        setSnackbar({ open: true, message: 'Assignment submitted!', severity: 'success' });
      }
      setSubmitOpen(false);
      loadSubmissions();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Submission failed.', severity: 'error' });
    }
  };

  const handleDelete = async (submissionId) => {
    try {
      await deleteSubmission(submissionId);
      setSnackbar({ open: true, message: 'Submission deleted.', severity: 'success' });
      loadSubmissions();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Delete failed.', severity: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Assignments</Typography>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 2.5,
          minHeight: 40,
          '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
          '& .MuiTab-root': { minHeight: 40, fontWeight: 600 },
        }}
      >
        <Tab label="Assignments" />
        <Tab label="My submissions" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : tab === 0 ? (
        assignments.length > 0 ? (
          <Grid container spacing={2} component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
            {assignments.map((a) => (
              <Grid item xs={12} sm={6} md={4} key={a.id}>
                <MotionPaper
                  variants={fadeInUp}
                  {...hoverLift}
                  elevation={0}
                  sx={{
                    p: 2.5, borderRadius: '18px', height: '100%', display: 'flex', flexDirection: 'column',
                    border: '1px solid', borderColor: 'divider',
                    '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.4), boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.14)}` },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{a.title || a.topic}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {a.course_name}{a.batch_name && ` — ${a.batch_name}`}
                  </Typography>
                  {a.due_date && (
                    <Chip
                      size="small"
                      label={`Due ${a.due_date}`}
                      sx={{ mb: 2, alignSelf: 'flex-start', backgroundColor: alpha(tokens.amber, 0.16), color: '#a86500' }}
                    />
                  )}
                  <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                    <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewDetail(a)}>View</Button>
                    <Button size="small" variant="contained" startIcon={<UploadFileIcon />} onClick={() => handleOpenSubmit(a)}>
                      Submit
                    </Button>
                  </Box>
                </MotionPaper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <EmptyState
            icon={<AssignmentOutlinedIcon />}
            title="No assignments yet"
            subtitle="New assignments from your teachers will show up here."
          />
        )
      ) : submissions.length > 0 ? (
        <Grid container spacing={2} component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
          {submissions.map((s) => {
            const meta = statusMeta(s.status);
            return (
              <Grid item xs={12} sm={6} md={4} key={s.id}>
                <MotionPaper
                  variants={fadeInUp}
                  {...hoverLift}
                  elevation={0}
                  sx={{
                    p: 2.5, borderRadius: '18px', height: '100%', display: 'flex', flexDirection: 'column',
                    border: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>{s.assignment_title || s.title}</Typography>
                  <Chip
                    size="small"
                    icon={meta.icon}
                    label={s.status || 'Submitted'}
                    sx={{ mb: 1, alignSelf: 'flex-start', backgroundColor: alpha(meta.color, 0.14), color: meta.color, textTransform: 'capitalize' }}
                  />
                  {s.score != null && <Typography variant="body2" sx={{ fontWeight: 600 }}>Score: {s.score}</Typography>}
                  {s.review && <Typography variant="body2" color="text.secondary">Review: {s.review}</Typography>}
                  <Box sx={{ mt: 'auto', display: 'flex', gap: 1, pt: 1 }}>
                    <IconButton size="small" onClick={() => handleViewSubmission(s.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenSubmit({ id: s.assignment_id || s.assignment }, s)}
                    >
                      <UploadFileIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </MotionPaper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <EmptyState
          icon={<AssignmentTurnedInIcon />}
          title="No submissions yet"
          subtitle="Submit an assignment and it'll show up here with its status and score."
        />
      )}

      {/* Detail Modal (assignment topic OR submission, depending on entry point) */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} closeAfterTransition>
        <Fade in={detailOpen}>
          <MotionBox
            sx={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: { xs: '92vw', sm: 480 }, bgcolor: 'background.paper', borderRadius: '18px',
              boxShadow: 24, p: 3, outline: 'none',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">{selectedDetail?.title || selectedDetail?.assignment_title}</Typography>
              <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selectedDetail?.description || selectedDetail?.note}
            </Typography>
            {selectedDetail?.score != null && <Typography variant="body2" sx={{ fontWeight: 600 }}>Score: {selectedDetail.score}</Typography>}
            {selectedDetail?.review && <Typography variant="body2">Teacher review: {selectedDetail.review}</Typography>}
            {selectedDetail?.pdf_file && (
              <Button
                sx={{ mt: 2 }}
                variant="outlined"
                href={getAssignmentPdfUrl(selectedDetail.pdf_file)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View PDF
              </Button>
            )}
          </MotionBox>
        </Fade>
      </Modal>

      {/* Submit / Update Modal */}
      <Modal open={submitOpen} onClose={() => setSubmitOpen(false)} closeAfterTransition>
        <Fade in={submitOpen}>
          <MotionBox
            sx={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: { xs: '92vw', sm: 420 }, bgcolor: 'background.paper', borderRadius: '18px',
              boxShadow: 24, p: 3, outline: 'none',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">{editingSubmissionId ? 'Update submission' : 'Submit assignment'}</Typography>
              <IconButton onClick={() => setSubmitOpen(false)}><CloseIcon /></IconButton>
            </Box>
            <Button
              component="label" variant="outlined" startIcon={<UploadFileIcon />} fullWidth
              sx={{ mb: 2, py: 1.2, borderStyle: 'dashed', borderRadius: '12px' }}
            >
              {file ? file.name : 'Choose file'}
              <input type="file" hidden onChange={(e) => setFile(e.target.files[0])} />
            </Button>
            <TextField
              label="Note (optional)" fullWidth multiline rows={3}
              value={note} onChange={(e) => setNote(e.target.value)} sx={{ mb: 2 }}
            />
            <Button variant="contained" fullWidth onClick={handleSubmit} sx={{ py: 1.1 }}>
              {editingSubmissionId ? 'Update' : 'Submit'}
            </Button>
          </MotionBox>
        </Fade>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Assignments;
